import React, { useState, useEffect, useCallback } from 'react';
import { SingAlongService } from '../services/SingAlongService';
import { Plus, Trash2, Music, Search, X, Loader as Loader2, Package } from 'lucide-react';

interface Bundle {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  songs: { id: string; song_id: string; title: string; artist: string }[];
}

interface Song {
  id: string;
  title: string;
  artist: string;
  cover_url: string | null;
}

export const AdminSingAlongBundles: React.FC = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const [songSearch, setSongSearch] = useState('');
  const [allSongs, setAllSongs] = useState<Song[]>([]);
  const [songsLoading, setSongsLoading] = useState(true);

  const loadBundles = useCallback(async () => {
    try {
      const data = await SingAlongService.adminLoadBundles();
      setBundles(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSongs = useCallback(async () => {
    try {
      const data = await SingAlongService.adminGetAllSongs();
      setAllSongs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSongsLoading(false);
    }
  }, []);

  useEffect(() => { loadBundles(); loadSongs(); }, [loadBundles, loadSongs]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await SingAlongService.adminCreateBundle(newName.trim(), newDesc.trim() || null);
      setNewName(''); setNewDesc(''); setShowCreate(false);
      await loadBundles();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (bundleId: string, currentState: boolean) => {
    await SingAlongService.adminToggleBundleActive(bundleId, currentState);
    await loadBundles();
  };

  const handleDeleteBundle = async (bundleId: string) => {
    await SingAlongService.adminDeleteBundle(bundleId);
    if (selectedBundle === bundleId) setSelectedBundle(null);
    await loadBundles();
  };

  const filteredSongs = allSongs.filter((song) => {
    if (!songSearch.trim()) return true;
    const q = songSearch.toLowerCase();
    return song.title.toLowerCase().includes(q) || song.artist.toLowerCase().includes(q);
  });

  const addSongToBundle = async (songId: string) => {
    if (!selectedBundle) return;
    const bundle = bundles.find(b => b.id === selectedBundle);
    if (bundle?.songs.some(s => s.song_id === songId)) return;
    await SingAlongService.adminAddSongToBundle(selectedBundle, songId, bundle?.songs.length || 0);
    await loadBundles();
  };

  const removeSongFromBundle = async (bundleSongId: string) => {
    await SingAlongService.adminRemoveSongFromBundle(bundleSongId);
    await loadBundles();
  };

  const activeBundle = bundles.find(b => b.id === selectedBundle);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Sing-Along Bundles</h2>
          <p className="text-sm text-white/50 mt-1">Manage song bundles available for consumer kiosk passes</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-violet-600 text-white rounded-lg font-semibold text-sm hover:bg-violet-700 transition flex items-center gap-2"
        >
          <Plus size={16} /> New Bundle
        </button>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-slate-800 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-lg mb-4">Create Bundle</h3>
            <div className="space-y-3">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Bundle name"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/30"
              />
              <textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Description (optional)"
                rows={2}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/30 resize-none"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-2.5 bg-white/5 border border-white/15 rounded-lg font-medium text-white/60"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !newName.trim()}
                className="flex-1 py-2.5 bg-violet-600 text-white rounded-lg font-semibold disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">Bundles</h3>
          {bundles.length === 0 ? (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-8 text-center text-white/40">
              <Package size={32} className="mx-auto mb-2 opacity-50" />
              <p>No bundles yet. Create one to get started.</p>
            </div>
          ) : (
            bundles.map((bundle) => (
              <div
                key={bundle.id}
                onClick={() => setSelectedBundle(bundle.id)}
                className={`bg-white/[0.03] border rounded-xl p-4 cursor-pointer transition ${
                  selectedBundle === bundle.id ? 'border-violet-500/50 bg-violet-500/5' : 'border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{bundle.name}</div>
                    <div className="text-xs text-white/40 mt-0.5">{bundle.songs.length} songs</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleActive(bundle.id, bundle.is_active); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold transition ${
                        bundle.is_active ? 'bg-green-500/15 text-green-400' : 'bg-white/5 text-white/40'
                      }`}
                    >
                      {bundle.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteBundle(bundle.id); }}
                      className="p-1.5 text-white/30 hover:text-red-400 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">
            {activeBundle ? `Songs in "${activeBundle.name}"` : 'Select a bundle'}
          </h3>

          {activeBundle ? (
            <div className="space-y-3">
              <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                {activeBundle.songs.length === 0 ? (
                  <div className="p-6 text-center text-white/40 text-sm">No songs yet. Search below to add.</div>
                ) : (
                  <div className="divide-y divide-white/5 max-h-60 overflow-y-auto">
                    {activeBundle.songs.map((song) => (
                      <div key={song.id} className="flex items-center justify-between px-4 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <Music size={14} className="text-white/30 shrink-0" />
                          <span className="text-sm truncate">{song.title}</span>
                          <span className="text-xs text-white/30 truncate">{song.artist}</span>
                        </div>
                        <button
                          onClick={() => removeSongFromBundle(song.id)}
                          className="p-1 text-white/30 hover:text-red-400 transition shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="text"
                  value={songSearch}
                  onChange={(e) => setSongSearch(e.target.value)}
                  placeholder="Filter songs..."
                  className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/15 rounded-lg text-sm text-white placeholder-white/30"
                />
              </div>

              {songsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-white/40" />
                </div>
              ) : (
                <div className="bg-white/[0.03] border border-white/10 rounded-xl max-h-72 overflow-y-auto divide-y divide-white/5">
                  {filteredSongs.length === 0 ? (
                    <div className="p-4 text-center text-white/40 text-sm">No songs found.</div>
                  ) : (
                    filteredSongs.map((song) => {
                      const alreadyAdded = activeBundle.songs.some(s => s.song_id === song.id);
                      return (
                        <div key={song.id} className="flex items-center justify-between px-4 py-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <Music size={14} className="text-white/30 shrink-0" />
                            <span className="text-sm truncate">{song.title}</span>
                            <span className="text-xs text-white/30 truncate">{song.artist}</span>
                          </div>
                          <button
                            onClick={() => addSongToBundle(song.id)}
                            disabled={alreadyAdded}
                            className="px-2.5 py-1 rounded text-xs font-medium bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 transition disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                          >
                            {alreadyAdded ? 'Added' : '+ Add'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-8 text-center text-white/40">
              <p className="text-sm">Select a bundle to manage its songs</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSingAlongBundles;
